import { Link } from "react-router-dom";
import { computeBmiTdee, bmiCategory, activityLabel, ACTIVITY_OPTIONS } from "@/lib/bmiTdee";
import { Scales, Flame, Heartbeat, WarningCircle, Info } from "@phosphor-icons/react";

/**
 * Compact card showing BMI + BMR (Katch-McArdle) + TDEE.
 * Rendered in the measurements section (admin + client).
 *
 * Props:
 *  - client: { user_id, height_cm, activity_level }
 *  - latestMeasurement: { peso_kg, massa_magra_kg } | null
 *  - editHref?: string (admin-only edit link when data is missing)
 */
export default function BmiTdeeCard({ client, latestMeasurement, editHref }) {
  const pesoKg = latestMeasurement?.peso_kg || 0;
  const massaMagraKg = latestMeasurement?.massa_magra_kg || 0;
  const heightCm = client?.height_cm || 0;
  const activityLevel = client?.activity_level || null;

  const { bmi, bmr, tdee, factor } = computeBmiTdee({
    pesoKg, heightCm, massaMagraKg, activityLevel,
  });
  const cat = bmiCategory(bmi);

  const missing = [];
  if (!heightCm) missing.push("altezza");
  if (!latestMeasurement) missing.push("un rilievo");
  else if (!massaMagraKg) missing.push("body fat");
  if (!activityLevel) missing.push("livello di attività");

  return (
    <div
      data-testid="bmi-tdee-card"
      className="rounded-2xl border border-[color:var(--border)] p-5 bg-[color:var(--bg-primary)]"
    >
      <div className="flex items-center justify-between">
        <div className="label-tiny inline-flex items-center gap-1.5">
          <Heartbeat size={14} weight="duotone" style={{ color: "var(--accent)" }} />
          Composizione &amp; fabbisogno energetico
        </div>
        {activityLevel && (
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">
            {activityLabel(activityLevel)} · ×{factor}
          </div>
        )}
      </div>

      {missing.length > 0 && (
        <div
          data-testid="bmi-tdee-missing"
          className="mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-xs"
          style={{ background: "var(--bg-secondary)", color: "var(--text-secondary)" }}
        >
          <WarningCircle size={14} weight="fill" style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
          <div>
            Manca <span className="font-medium text-[color:var(--text-primary)]">{missing.join(", ")}</span> per completare il calcolo.
            {editHref && (
              <>
                {" "}
                <Link to={editHref} className="underline hover:text-[color:var(--accent)]">
                  Compila ora
                </Link>
              </>
            )}
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
        {/* BMI */}
        <div className="rounded-xl p-2.5 sm:p-3 min-w-0" style={{ background: "var(--bg-secondary)" }}>
          <div className="flex items-center gap-1 label-tiny">
            <Scales size={11} weight="duotone" style={{ color: "var(--accent)" }} />
            BMI
          </div>
          <div className="hidden sm:block text-[9px] text-[color:var(--text-secondary)] italic mt-0.5">
            Indice di massa corporea
          </div>
          <div className="mt-1 font-sans text-lg sm:text-3xl font-medium tabular-nums leading-tight" data-testid="bmi-value">
            {bmi != null ? String(bmi).replace(".", ",") : "—"}
          </div>
          {cat ? (
            <div
              data-testid="bmi-category"
              className="mt-1 inline-block text-[9px] sm:text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full whitespace-nowrap max-w-full truncate"
              style={{ background: cat.color, color: "#FCFAF8" }}
            >
              {cat.label}
            </div>
          ) : (
            <div className="mt-1 text-[9px] sm:text-[10px] text-[color:var(--text-secondary)]">— kg/m²</div>
          )}
        </div>

        {/* BMR */}
        <div className="rounded-xl p-2.5 sm:p-3 min-w-0" style={{ background: "var(--bg-secondary)" }}>
          <div className="flex items-center gap-1 label-tiny">
            <Flame size={11} weight="duotone" style={{ color: "var(--accent)" }} />
            BMR
          </div>
          <div className="hidden sm:block text-[9px] text-[color:var(--text-secondary)] italic mt-0.5">
            Metabolismo basale
          </div>
          <div className="mt-1 font-sans text-lg sm:text-3xl font-medium tabular-nums leading-tight" data-testid="bmr-value">
            {bmr != null ? bmr : "—"}
          </div>
          <div className="mt-1 text-[9px] sm:text-[10px] text-[color:var(--text-secondary)] truncate">
            kcal
            <span className="hidden sm:inline"> · Katch-McArdle</span>
          </div>
        </div>

        {/* TDEE */}
        <div
          className="rounded-xl p-2.5 sm:p-3 min-w-0"
          style={{ background: "var(--text-primary)", color: "var(--bg-primary)" }}
        >
          <div className="flex items-center gap-1 label-tiny" style={{ color: "rgba(252,250,248,0.65)" }}>
            <Flame size={11} weight="duotone" />
            TDEE
          </div>
          <div className="hidden sm:block text-[9px] italic mt-0.5" style={{ color: "rgba(252,250,248,0.55)" }}>
            Fabbisogno giornaliero
          </div>
          <div className="mt-1 font-sans text-lg sm:text-3xl font-medium tabular-nums leading-tight" data-testid="tdee-value">
            {tdee != null ? tdee : "—"}
          </div>
          <div className="mt-1 text-[9px] sm:text-[10px] truncate" style={{ color: "rgba(252,250,248,0.55)" }}>
            kcal
            <span className="hidden sm:inline"> · mantenimento</span>
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-start gap-1.5 text-[10px] text-[color:var(--text-secondary)] italic">
        <Info size={11} weight="duotone" style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          BMR = 370 + 21,6 × massa magra (kg). TDEE = BMR × fattore attività.
        </span>
      </div>
    </div>
  );
}
